/**
 * Authentication Context
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { amplifyAuthService } from './amplifyAuthService';
import { type AuthState, type AuthAction, type AuthError } from './types';

// Initial state
const initialState: AuthState = {
  session: null,
  isLoading: true,
  error: null,
  isInitialized: false,
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        session: action.payload,
        isLoading: false,
        error: null,
        isInitialized: true,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        session: null,
        isLoading: false,
        error: action.payload,
        isInitialized: true,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        session: null,
        isLoading: false,
        error: null,
        isInitialized: true,
      };
    case 'AUTH_INITIALIZED':
      return {
        ...state,
        isLoading: false,
        isInitialized: true,
      };
    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context type
interface AuthContextType extends AuthState {
  isAuthenticated: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (username: string, email: string, password: string, name?: string) => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  resendConfirmationCode: (username: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmForgotPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  respondToNewPasswordChallenge: (newPassword: string, userAttributes?: Record<string, string>) => Promise<void>;
  getPendingChallenge: () => { challengeName: string; challengeParameters: any } | null;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is authenticated
        const isAuth = await amplifyAuthService.isAuthenticated();
        if (isAuth) {
          const session = await amplifyAuthService.getCurrentSession();
          dispatch({ type: 'AUTH_SUCCESS', payload: session });
        } else {
          dispatch({ type: 'AUTH_INITIALIZED' });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      }
    };

    // Set up auth state listener
    const removeListener = amplifyAuthService.addAuthStateListener((session) => {
      if (session) {
        dispatch({ type: 'AUTH_SUCCESS', payload: session });
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    });

    initializeAuth();

    // Cleanup listener on unmount
    return () => {
      removeListener();
    };
  }, []);

  // Sign in with username and password
  const signIn = async (username: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const session = await amplifyAuthService.signIn({ username, password });
      dispatch({ type: 'AUTH_SUCCESS', payload: session });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Sign up new user
  const signUp = async (username: string, email: string, password: string, name?: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      await amplifyAuthService.signUp({ username, email, password, name });
      dispatch({ type: 'AUTH_INITIALIZED' });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Confirm sign up
  const confirmSignUp = async (username: string, code: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      await amplifyAuthService.confirmSignUp(username, code);
      dispatch({ type: 'AUTH_INITIALIZED' });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Resend confirmation code
  const resendConfirmationCode = async (username: string): Promise<void> => {
    try {
      await amplifyAuthService.resendConfirmationCode(username);
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Forgot password
  const forgotPassword = async (username: string): Promise<void> => {
    try {
      await amplifyAuthService.forgotPassword({ username });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Confirm forgot password
  const confirmForgotPassword = async (username: string, code: string, newPassword: string): Promise<void> => {
    try {
      await amplifyAuthService.confirmForgotPassword({ username, code, newPassword });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Change password
  const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    if (!state.session) {
      throw new Error('User not authenticated');
    }
    try {
      await amplifyAuthService.changePassword(oldPassword, newPassword);
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Sign out
  const signOut = async (): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      await amplifyAuthService.signOut();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      // Even if sign out fails on server, clear local session
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };


  // Refresh token
  const refreshToken = async (): Promise<void> => {
    if (!state.session) {
      throw new Error('No session to refresh');
    }
    try {
      const session = await amplifyAuthService.getCurrentSession();
      dispatch({ type: 'AUTH_SUCCESS', payload: session });
    } catch (error) {
      // Refresh failed, sign out user
      await signOut();
      throw error;
    }
  };

  // Respond to new password challenge
  const respondToNewPasswordChallenge = async (newPassword: string, userAttributes?: Record<string, string>): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const session = await amplifyAuthService.respondToNewPasswordChallenge(newPassword, userAttributes);
      dispatch({ type: 'AUTH_SUCCESS', payload: session });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error as AuthError });
      throw error;
    }
  };

  // Get pending challenge
  const getPendingChallenge = (): { challengeName: string; challengeParameters: any } | null => {
    return amplifyAuthService.getPendingChallenge();
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    ...state,
    isAuthenticated: state.session?.isAuthenticated || false,
    signIn,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    respondToNewPasswordChallenge,
    getPendingChallenge,
    signOut,
    refreshToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
